import { ConductorAllChildrenActive, Router, ScreenBase } from "@frui.ts/screens";
import {
  NOTIFICATION_SERVICE_CONNECTION_ID_UNDEFINED,
  NOTIFICATION_SERVICE_INIT_FAILURE,
  NOTIFICATION_SERVICE_START_FAILURE,
  NOTIFICATION_SERVICE_STOP_FAILURE,
  OUTAGE_ACTIVATED,
} from "../../repositories/order/constants";
import LocalizationService from "../../services/localizationService";
import AppRouter from "services/appRouter";
import { scrollToElement, scrollToTop } from "../../utils/scrollTo";
import { action, computed, observable, reaction } from "mobx";
import * as types from "viewModels/common/types";
import SummaryViewModel from "./summaryMultiEshopViewModel";
import { PaymentType } from "../../models/payment";
import BankTransferViewModel from "../common/payment/bankTransferViewModel";
import PaymentService from "../../services/payment/paymentService";
import React from "react";
import EventBus, {
  googlePayPayment3DSecureLoaded,
  googlePayPaymentBrowserFingerprintLoaded,
  googlePayPaymentStatusChanged,
  multiEshopPaymentUpdate,
  paymentCompleteAlert,
  paymentFailed,
  paymentValidSinceInvalid,
  submitGooglePayPaymentDeferred,
  submitGooglePayPaymentFailure,
  submitGooglePayPaymentSuccess,
} from "../../services/eventBus";
import { MultiEshopModuleNavigationName } from "./multiEshopViewModel";
import OutageService from "../../services/outageService";
import SelectPaymentMethodViewModel from "../common/payment/selectPaymentMethodViewModel";
import { getStatusSteps } from "./multiEshopStatusSteps";
import BatchOrderViewModel from "./batchOrderViewModel";
import StatusViewModel from "../common/payment/statusViewModel";
import PaymentResult from "../../models/paymentResult";
import * as Sentry from "@sentry/react";
import { PayGateCommonPaymentStatus } from "models/googlePayInitPaymentResponse";
import ValiditySelectViewModel from "viewModels/eshop/order/components/validitySelectViewModel";
import EnumService from "services/enumService";
import ChargeValidityRuleResponse from "entities/chargeValidityRuleResponse";
import ChargeOrderService from "services/chargeOrderService";
import ChargeOrder, { ChargeOrderStatus, ChargeOrderValidationRules } from "models/chargeOrder";
import { validate } from "@frui.ts/validation";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import { isMobile } from "react-device-detect";
import { interfaces } from "inversify";
import ChargeOrderResponse from "entities/chargeOrderResponse";
import TermsAgreementViewModel from "viewModels/eshop/order/components/termAgreementViewModel";
import ChargeOrderRepository from "repositories/chargeOrderRepository";
import TimeService from "services/timeService";

export const PaymentMultiEshopViewModelNavigationName = "payment";

@Router.registerRoute({ name: "payment", route: PaymentMultiEshopViewModelNavigationName })
export default class PaymentMultiEshopViewModel extends ConductorAllChildrenActive<ScreenBase> {
  private unsubscription: Array<() => void> = [];
  private documentPathRegex = /%documentPath%/g;

  tg = (code: string) => this.localizationService.translateGeneral(`multiEshop.${code}`);

  @observable formElement?: HTMLFormElement;
  @observable summaryViewModel: SummaryViewModel;
  @observable selectPaymentMethodViewModel: SelectPaymentMethodViewModel;
  @observable termsAgreementViewModel: TermsAgreementViewModel;
  @observable errorMessage?: types.ErrorMessage;
  @observable showPaymentLoadingModal = false;
  @observable showGooglePay3DSecureModal = false;
  @observable googlePay3DSecureUrl?: string;
  @observable chargeOrderStatus?: ChargeOrderStatus;
  public validSinceSelectViewModel: ValiditySelectViewModel;

  statusNavigation = getStatusSteps();

  constructor(
    public chargeOrder: ChargeOrder,
    public localizationService: LocalizationService,
    public outageService: OutageService,
    public enumService: EnumService,
    public chargeOrderService: ChargeOrderService,
    private timeService: TimeService,
    public repository: ChargeOrderRepository,
    private paymentService: PaymentService,
    private summaryViewModelFactory: ReturnType<typeof SummaryViewModel.Factory>,
    private selectPaymentMethodViewModelFactory: ReturnType<typeof SelectPaymentMethodViewModel.Factory>,
    private termsAgreementViewModelFactory: ReturnType<typeof TermsAgreementViewModel.Factory>,
    private validSinceSelectVMFactory: ReturnType<typeof ValiditySelectViewModel.Factory>,
    private router: AppRouter,
    private eventBus: EventBus
  ) {
    super();
    this.navigationName = PaymentMultiEshopViewModelNavigationName;
    this.eventBus.subscribe(paymentFailed, this.paymentFailed);
    this.eventBus.subscribe(paymentValidSinceInvalid, this.paymentValidSinceInvalid);
    this.validSinceSelectViewModel = this.validSinceSelectVMFactory(this.chargeOrderService.chargeOrderItem);
    this.validSinceSelectViewModel.getPostponedValidityMaxDays(
      this.timeService.currentServerTime ?? new Date(),
      this.enumService.values("charge_validity_rules") as ChargeValidityRuleResponse[]
    );
    attachAutomaticBlurValidator(this.chargeOrder, ChargeOrderValidationRules);
  }

  protected onInitialize() {
    const handlePopState = () => {
      this.resetTermsAgreement();
    };

    // Přidání listeneru pro popstate
    window.addEventListener("popstate", handlePopState);

    // Uložení odregistrovací funkce
    this.unsubscription.push(() => window.removeEventListener("popstate", handlePopState));
    this.initChildren();
    this.paymentService.resetRedirect();
    this.unsubscription.push(
      this.eventBus.subscribe(submitGooglePayPaymentFailure, this.googlePayPaymentFailure),
      this.eventBus.subscribe(submitGooglePayPaymentSuccess, this.googlePayPaymentSuccess),
      this.eventBus.subscribe(submitGooglePayPaymentDeferred, this.googlePayPaymentDeferred),
      this.eventBus.subscribe(googlePayPaymentBrowserFingerprintLoaded, this.googlePayPaymentBrowserFingerprintLoaded),
      this.eventBus.subscribe(googlePayPayment3DSecureLoaded, this.googlePayPayment3DSecureLoaded),
      this.eventBus.subscribe(googlePayPaymentStatusChanged, this.googlePayPaymentStatusChanged),
      reaction(() => [this.chargeOrder._termsAgreement, this.chargeOrder.paymentType], this.updateChargeOrderItem)
    );
    super.onInitialize();
  }

  protected async onDeactivate(isClosing: boolean): Promise<void> {
    if (isClosing) {
      // Odpojení všech uložených listenerů
      this.unsubscription.forEach(unsubscribe => unsubscribe());
      this.unsubscription = [];
    }
    // Zavolejte původní implementaci v základní třídě
    await super.onDeactivate(isClosing);
  }

  protected async onActivate(): Promise<any> {
    if (isMobile && this.stepper) {
      scrollToElement(this.stepper);
    }

    this.errorMessage = undefined;

    this.chargeOrderService.chargeOrder._origin = "multiple";
    this.checkRecapStoreData();
    this.unsubscription.push(
      reaction(
        () => this.chargeOrderService.isWholeOrderValid,
        isWholeOrderValid => {
          if (isWholeOrderValid && this.errorMessage === "invalidData") {
            this.errorMessage = undefined;
          }
        }
      )
    );
    super.onActivate();
  }

  translateAttribute(code: string) {
    return this.localizationService.translateAttribute("payment", code);
  }

  @computed
  get stepper() {
    return window.document.getElementById("stepper");
  }

  @computed
  get transferDisabled() {
    return this.chargeOrderService.isTransferDisabled;
  }

  @computed
  get isOrderComplete() {
    return this.chargeOrderStatus === ChargeOrderStatus.Paid || this.chargeOrderStatus === ChargeOrderStatus.PaidBankTransfer;
  }

  @action.bound
  initChildren() {
    this.children.clear();
    this.summaryViewModel = this.summaryViewModelFactory(true, false, this.onContinue, this.onBack);
    this.selectPaymentMethodViewModel = this.selectPaymentMethodViewModelFactory(this.chargeOrder);
    this.termsAgreementViewModel = this.termsAgreementViewModelFactory(this.chargeOrder);
    this.children.push(this.selectPaymentMethodViewModel, this.termsAgreementViewModel, this.summaryViewModel);
  }

  @action.bound
  public onBack() {
    this.chargeOrderService.persistStoredChargeOrder(this.chargeOrder);
    this.router.navigate("contact");
  }

  @action.bound
  private resetTermsAgreement() {
    this.chargeOrder._termsAgreement = false;
  }

  @action.bound
  onContinue = async (errorMessage: types.ErrorMessage, paymentData?: google.payments.api.PaymentData) => {
    this.errorMessage = undefined;
    if (!this.chargeOrderService.validateMultiOrder() || !validate(this.chargeOrder)) {
      return;
    }

    await this.payment(paymentData);
  };

  @action.bound
  async getOrderStatus(id: string) {
    const order = await this.repository.get(id);
    this.chargeOrderStatus = order.status;
  }

  @action.bound translate(code: string) {
    return this.localizationService.translateGeneral(`multiEshop.${code}`);
  }

  @action.bound
  onOrderIsCompleteCheck(orderId: string) {
    this.eventBus.publish(paymentCompleteAlert({ orderId }));
  }

  @action.bound
  paymentLoadingModalToggle = (open: boolean) => {
    this.showPaymentLoadingModal = open;
  };

  @action.bound
  protected paymentFailed(event: ReturnType<typeof paymentFailed>) {
    switch (event.payload.message) {
      case NOTIFICATION_SERVICE_INIT_FAILURE:
      case NOTIFICATION_SERVICE_CONNECTION_ID_UNDEFINED:
      case NOTIFICATION_SERVICE_START_FAILURE:
      case NOTIFICATION_SERVICE_STOP_FAILURE:
        this.errorMessage = "websocket";
        Sentry.withScope(scope => {
          scope.setTag("websocket", "paymentFailed");
          const error = new Error(`Notification Service Error: ${event.payload.message}`);
          error.name = "NotificationServiceError";
          Sentry.captureException(error);
        });
        break;
      case OUTAGE_ACTIVATED:
        scrollToTop();
        break;
    }
    this.paymentLoadingModalToggle(false);
  }

  @action.bound
  protected paymentValidSinceInvalid(event: ReturnType<typeof paymentValidSinceInvalid>) {
    this.router.navigate(BatchOrderViewModel);
    this.paymentLoadingModalToggle(false);
  }

  @action.bound
  checkRecapStoreData() {
    const { paymentType, _termsAgreement } = this.chargeOrderService.chargeOrder;

    this.chargeOrder.paymentType = paymentType;
    this.chargeOrder._termsAgreement = _termsAgreement;
  }

  @action.bound
  updateChargeOrderItem() {
    const chargeOrder = this.chargeOrderService.chargeOrder;
    const { _termsAgreement, paymentType } = this.chargeOrder;

    chargeOrder.paymentType = paymentType;
    chargeOrder._termsAgreement = _termsAgreement;

    this.eventBus.publish(multiEshopPaymentUpdate({ paymentType: paymentType, termsAgreement: _termsAgreement }));

    this.chargeOrderService.persistStoredChargeOrder(chargeOrder);
  }

  @action.bound
  private payment = async (paymentData: google.payments.api.PaymentData | undefined) => {
    this.errorMessage = undefined;
    if (!this.chargeOrderService.validateMultiOrder()) {
      return;
    }

    if (this.chargeOrder.paymentType === PaymentType.Transfer) {
      await this.payOrder(this.summaryViewModel.turnstileViewModel.turnstileToken, paymentData);
      return;
    }

    await this.payOrder(undefined, paymentData);
  };

  @action.bound
  protected googlePayPaymentFailure() {
    this.paymentLoadingModalToggle(false);
    this.chargeOrder._status = ChargeOrderStatus.Failed;
    this.chargeOrderService.persistChargeOrder();
    this.errorMessage = "paymentFailure";
    this.showGooglePay3DSecureModal = false;
    this.googlePay3DSecureUrl = undefined;
    this.initChildren();
    scrollToTop();
  }

  @action.bound
  protected googlePayPaymentSuccess() {
    this.paymentLoadingModalToggle(false);
    this.chargeOrder._status = ChargeOrderStatus.Paid;
    this.chargeOrderService.persistChargeOrder();
    this.showGooglePay3DSecureModal = false;
    this.router.navigate(StatusViewModel, { orderId: this.chargeOrder.id }, { result: PaymentResult.SUCCESS });
    scrollToTop();
  }

  @action.bound
  protected googlePayPaymentDeferred() {
    this.paymentLoadingModalToggle(false);
    this.chargeOrder._status = ChargeOrderStatus.Deferred;
    this.chargeOrderService.persistChargeOrder();
    this.showGooglePay3DSecureModal = false;
    this.router.navigate(StatusViewModel, { orderId: this.chargeOrder.id }, { result: PaymentResult.DEFERRED });
    scrollToTop();
  }

  @action.bound
  protected googlePayPaymentBrowserFingerprintLoaded() {
    return this.paymentService.confirmGooglePayPayment(this.chargeOrder.id);
  }

  @action.bound
  protected googlePayPayment3DSecureLoaded(result: { payload: { url: string } }) {
    this.showGooglePay3DSecureModal = true;
    this.googlePay3DSecureUrl = result.payload.url;
  }

  @action.bound
  googlePayPaymentStatusChanged(result: { payload: { status: PayGateCommonPaymentStatus } }) {
    if (result.payload.status != PayGateCommonPaymentStatus.IN_PROGRESS) {
      this.paymentService.finalizeGooglePayPayment(result.payload.status);
    }
  }

  @action.bound
  resetChargeOrder() {
    this.chargeOrderService.reset();
    this.router.navigate(BatchOrderViewModel);
  }

  @action.bound
  private payOrder = async (
    turnstileToken: string | undefined | null,
    paymentData: google.payments.api.PaymentData | undefined
  ) => {
    this.paymentLoadingModalToggle(true);
    await this.chargeOrderService.payOrder(turnstileToken, false, async (chargeOrder: ChargeOrderResponse) => {
      if (this.errorMessage) {
        this.paymentLoadingModalToggle(false);
        return;
      }
      await this.chargeOrderService.updateOrder(chargeOrder);
      this.chargeOrder.id = chargeOrder.id;
      const order = await this.chargeOrderService.chargeOrder;
      switch (order.paymentType) {
        case PaymentType.PayGate:
          await this.paymentService.payGateRedirect(chargeOrder.id, MultiEshopModuleNavigationName).then(() => {
            this.paymentLoadingModalToggle(false);
          });
          break;
        case PaymentType.Transfer:
          this.paymentLoadingModalToggle(false);
          const orderId = this.chargeOrderService.orderPaidByBankTransfer();
          scrollToElement(this.formElement);
          this.router.navigate(BankTransferViewModel, { orderId });
          break;
        case PaymentType.GooglePay:
          try {
            if (paymentData === undefined) {
              throw Error("payment data not defined");
            }
            await this.paymentService.submitGooglePayment(chargeOrder.id, paymentData);
          } catch (error) {
            Sentry.captureException(error);
            this.googlePayPaymentFailure();
          }
          break;
        default:
          this.paymentLoadingModalToggle(false);
          break;
      }
    });
  };

  static Factory({ container }: interfaces.Context) {
    return (chargeOrder: ChargeOrder) =>
      new PaymentMultiEshopViewModel(
        chargeOrder,
        container.get(LocalizationService),
        container.get(OutageService),
        container.get(EnumService),
        container.get(ChargeOrderService),
        container.get(TimeService),
        container.get(ChargeOrderRepository),
        container.get(PaymentService),
        container.get(SummaryViewModel.Factory),
        container.get(SelectPaymentMethodViewModel.Factory),
        container.get(TermsAgreementViewModel.Factory),
        container.get(ValiditySelectViewModel.Factory),
        container.get(AppRouter),
        container.get(EventBus)
      );
  }
}
